import { useRef, useState, forwardRef } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "./ui/dialog";
import { Download, Share2, Crown, Sparkles, Loader2, Link2, Check } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { API } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { hideOnError } from "../lib/imgFallback";

/**
 * FounderShareCard — generates a downloadable 1080×1080 Instagram-square
 * share card for an Elder Founder. Opens in a Dialog with a live preview
 * and a "Download PNG" button.
 *
 * Used in two places:
 *   - FounderWallStatsCard (Elder's own /wallet view, "Share my card" CTA)
 *   - ElderProfile (any public visitor on /elders/{uid}, "Share" CTA)
 *
 * Props:
 *   elder: { name, businessName, elderIndex, tradition, lineageStatement,
 *            bio, avatarUrl, category, yearsInitiated, userId }
 *   stats?: { lifetimeViews?, liftPct? }   // optional stats overlay
 *   trigger?: ReactNode                    // custom trigger button
 */
export default function FounderShareCard({ elder, stats = null, trigger = null }) {
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!elder) return null;
  const displayName = elder.businessName || elder.name;
  const initials = (elder.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const avatarSrc = elder.avatarUrl?.startsWith("http")
    ? elder.avatarUrl
    : (elder.avatarUrl ? `${API}${elder.avatarUrl}` : null);
  const rankLabel = elder.elderIndex
    ? `#${String(elder.elderIndex).padStart(4, "0")} of 500`
    : "Verified Founder Elder";
  const pullquote = elder.lineageStatement || elder.bio;

  // Attribution-tagged share URL. Pasting THIS in IG/Twitter bios is what
  // makes the "card clickbacks" metric work — visitors arrive with
  // ?from=share-card so the backend tags them in share_card_clicks.
  // Always points to the canonical production domain so the link survives
  // anywhere the recipient opens it.
  const shareUrl = `${PUBLIC_BASE}/elders/${elder.userId}?from=share-card`;

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied — paste it in your bio");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#15102A",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `cauldron-founder-${displayName.replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Spotlight card saved to your downloads");
    } catch (e) {
      toast.error("Couldn't render the card — try again");
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            type="button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] px-3 py-2 rounded-sm border border-secondary/40 text-secondary hover:bg-secondary/10"
            data-testid="founder-share-card-open"
          >
            <Share2 size={12} strokeWidth={1.75} /> Share card
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        className="max-w-[640px] bg-card border-secondary/30 p-0 overflow-hidden"
        data-testid="founder-share-card-dialog"
      >
        <div className="p-5 border-b border-secondary/15">
          <div className="label-mystic text-secondary">Founder Spotlight Card</div>
          <DialogTitle className="font-serif text-2xl mt-1">Share your founder badge</DialogTitle>
          <DialogDescription className="text-xs text-foreground/60 mt-1">
            A 1,080 × 1,080 PNG perfect for Instagram, Twitter, or TikTok.
          </DialogDescription>
        </div>

        {/* Live preview — scaled down 50% so the dialog fits screens. The
            actual export renders at full 1080×1080 via pixelRatio: 2. */}
        <div className="bg-background/50 p-4 flex items-center justify-center">
          <div style={{ width: 540, height: 540, overflow: "hidden", position: "relative" }}>
            <div style={{
              transform: "scale(0.5)", transformOrigin: "top left",
              width: 1080, height: 1080,
            }}>
              <ShareCardArt
                ref={cardRef}
                displayName={displayName}
                initials={initials}
                avatarSrc={avatarSrc}
                rankLabel={rankLabel}
                tradition={elder.tradition}
                yearsInitiated={elder.yearsInitiated}
                category={elder.category}
                pullquote={pullquote}
                stats={stats}
                userId={elder.userId}
              />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-secondary/15 flex items-center gap-3 bg-background/30" data-testid="founder-share-card-link-row">
          <Link2 size={14} className="text-secondary shrink-0" strokeWidth={1.75} />
          <input
            type="text"
            readOnly
            value={shareUrl}
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent text-xs text-foreground/80 outline-none truncate"
            data-testid="founder-share-card-link-input"
          />
          <button
            onClick={copyShareUrl}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-sm border border-secondary/40 text-secondary hover:bg-secondary/10 shrink-0"
            data-testid="founder-share-card-copy-link"
          >
            {copied ? <><Check size={11} /> Copied</> : <><Link2 size={11} /> Copy</>}
          </button>
        </div>

        <div className="p-5 flex items-center justify-end gap-3 border-t border-secondary/15">
          <button
            onClick={() => setOpen(false)}
            className="text-xs uppercase tracking-[0.18em] text-foreground/65 hover:text-foreground px-3 py-2"
            data-testid="founder-share-card-cancel"
          >
            Cancel
          </button>
          <button
            onClick={download}
            disabled={downloading}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-primary/90 disabled:opacity-60"
            data-testid="founder-share-card-download"
          >
            {downloading ? (
              <><Loader2 size={13} className="animate-spin" /> Rendering…</>
            ) : (
              <><Download size={13} /> Download PNG</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// — The actual rendered card, sized at 1080×1080 with absolute-positioned
//   layers and forced sRGB colors (no Tailwind theme variables — html-to-image
//   sometimes drops CSS-variable values). Inline styles only.
const ShareCardArt = forwardRef(function ShareCardArt({
  displayName, initials, avatarSrc, rankLabel, tradition,
  yearsInitiated, category, pullquote, stats, userId,
}, ref) {
  return (
    <div
      ref={ref}
      data-testid="founder-share-card-art"
      style={{
        width: 1080, height: 1080, position: "relative", overflow: "hidden",
        background:
          "radial-gradient(ellipse at 22% 32%, rgba(217,92,20,0.35), transparent 55%), " +
          "radial-gradient(ellipse at 78% 70%, rgba(197,160,89,0.28), transparent 60%), " +
          "linear-gradient(180deg, #1A1530 0%, #15102A 70%, #0C0820 100%)",
        fontFamily: '"Cormorant Garamond", "Georgia", serif',
        color: "#F4EEDA",
        boxSizing: "border-box",
        padding: 88,
      }}
    >
      {/* Top-left badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 56 }}>
        <Crown size={20} color="#C5A059" />
        <div style={{
          letterSpacing: "0.32em", textTransform: "uppercase",
          fontSize: 13, color: "#C5A059", fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: 600,
        }}>
          Cauldron · Founder of the 500
        </div>
      </div>

      {/* Avatar + identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 48, marginBottom: 64 }}>
        <div style={{ position: "relative", width: 200, height: 200, flexShrink: 0 }}>
          <div style={{
            position: "absolute", inset: -10, borderRadius: "50%",
            background: "conic-gradient(from 140deg, #C5A059, #D95C14, #F4D58E, #C5A059, #D95C14)",
            padding: 5,
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            boxShadow: "0 0 40px rgba(217,92,20,0.6)",
          }} />
          <div style={{
            width: 200, height: 200, borderRadius: "50%", overflow: "hidden",
            background: "rgba(197,160,89,0.18)", border: "4px solid #15102A",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {avatarSrc ? (
              <img src={avatarSrc} alt="" crossOrigin="anonymous"
                   style={{ width: "100%", height: "100%", objectFit: "cover" }}
                   onError={hideOnError} />
            ) : (
              <span style={{ fontSize: 88, color: "#C5A059" }}>{initials}</span>
            )}
          </div>
          {/* Crown badge */}
          <div style={{
            position: "absolute", top: -10, right: -10, width: 56, height: 56,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #D95C14 0%, #C5A059 60%, #F4D58E 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 20px rgba(0,0,0,0.45), 0 0 0 4px #15102A",
            color: "#15102A",
          }}>
            <Crown size={28} strokeWidth={2.5} fill="#15102A" />
          </div>
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            letterSpacing: "0.22em", textTransform: "uppercase",
            fontSize: 16, color: "#C5A059",
            fontFamily: "Helvetica, Arial, sans-serif", fontWeight: 600,
          }}>
            {rankLabel}
          </div>
          <div style={{
            fontSize: 76, lineHeight: 1.02, marginTop: 16,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 500, color: "#F4EEDA",
            overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {displayName}
          </div>
          {(tradition || category) && (
            <div style={{
              marginTop: 14, fontSize: 18, color: "rgba(244,238,218,0.75)",
              fontFamily: "Helvetica, Arial, sans-serif",
              letterSpacing: "0.08em",
            }}>
              {[tradition, category, yearsInitiated ? `${yearsInitiated}+ yrs` : null]
                .filter(Boolean).join("  ·  ")}
            </div>
          )}
        </div>
      </div>

      {/* Pull-quote */}
      {pullquote && (
        <div style={{
          marginBottom: 64, paddingLeft: 36,
          borderLeft: "3px solid rgba(197,160,89,0.55)",
        }}>
          <div style={{
            fontSize: 30, lineHeight: 1.35, fontStyle: "italic",
            color: "rgba(244,238,218,0.92)",
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            maxHeight: 280, overflow: "hidden",
          }}>
            “{pullquote.slice(0, 280)}{pullquote.length > 280 ? "…" : ""}”
          </div>
        </div>
      )}

      {/* Optional stats */}
      {stats && (stats.lifetimeViews != null || stats.liftPct != null) && (
        <div style={{
          display: "flex", gap: 64, marginBottom: 56,
          alignItems: "baseline",
        }}>
          {stats.lifetimeViews != null && (
            <Stat label="Profile views" value={stats.lifetimeViews.toLocaleString()} />
          )}
          {stats.liftPct != null && (
            <Stat
              label="This week"
              value={`${stats.liftPct >= 0 ? "+" : ""}${stats.liftPct}%`}
              accent={stats.liftPct >= 0 ? "#C5A059" : "#D95C14"}
            />
          )}
        </div>
      )}

      {/* Bottom watermark + url */}
      <div style={{
        position: "absolute", left: 88, bottom: 64, right: 88,
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 24,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#C5A059" }}>
            <Sparkles size={16} />
            <div style={{
              letterSpacing: "0.32em", textTransform: "uppercase",
              fontSize: 13, fontFamily: "Helvetica, Arial, sans-serif",
              fontWeight: 600,
            }}>
              Cauldron
            </div>
          </div>
          <div style={{
            fontSize: 22, marginTop: 8,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: "rgba(244,238,218,0.7)", fontStyle: "italic",
          }}>
            The sacred marketplace
          </div>
        </div>
        <div style={{
          fontSize: 18, color: "rgba(244,238,218,0.55)",
          fontFamily: "Helvetica, Arial, sans-serif",
          letterSpacing: "0.06em",
        }}>
          cauldron.app/elders/{userId ? userId.slice(0, 8) : ""}?src=card
        </div>
      </div>
    </div>
  );
});

function Stat({ label, value, accent }) {
  return (
    <div>
      <div style={{
        letterSpacing: "0.22em", textTransform: "uppercase",
        fontSize: 13, color: "rgba(244,238,218,0.55)",
        fontFamily: "Helvetica, Arial, sans-serif", fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 56, marginTop: 6, color: accent || "#F4EEDA",
        fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500,
        lineHeight: 1,
      }}>
        {value}
      </div>
    </div>
  );
}
